import { PermissionGate } from "@components/PermissionGate";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { BsCheckLg } from "react-icons/bs";
import { GrAttachment } from "react-icons/gr";
import { HiArrowLeft } from "react-icons/hi";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  ComboboxItem,
  Group,
  Loader,
  LoadingOverlay,
  NumberInput,
  Select,
  Stack,
  Table,
  Tabs,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  useDisclosure,
  useDocumentTitle,
  usePrevious,
  useSessionStorage,
} from "@mantine/hooks";
import {
  IconCalendar,
  IconDeviceFloppy,
  IconInfoCircle,
  IconPencil,
  IconPhone,
  IconPlus,
  IconTrash,
  IconVideo,
  IconX,
} from "@tabler/icons-react";
import { useIsConnected } from "agora-rtc-react";

import { CreateAppointmentModal } from "@pages/Appointments/components/CreateAppointmentModal";
import { AddModal as AddLabModal } from "@pages/Patients/Laboratory/components/AddModal";
import { EditModal as LabEditModal } from "@pages/Patients/Laboratory/components/EditModal";
import { AddModal as AddMedicationModal } from "@pages/Patients/Medication/components/AddModal";
import { EditModal as EditMedicationModal } from "@pages/Patients/Medication/components/EditModal";
import { MedicalHistoryCard } from "@pages/Patients/Profile/components/MedicalHistoryCard";
import { SurgicalHistoryCard } from "@pages/Patients/Profile/components/SurgicalHistoryCard";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { ConsultationServiceSelect } from "@components/ConsultationServiceSelect";
import { openDeleteModal } from "@components/DeleteModal";
import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";

import { calculateBMI } from "@utils/calculateBMI";
import {
  combineDateWithCurrentTime,
  formatDateToString,
} from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import {
  showErrorNotification,
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";
import { truncateWithEllipsis } from "@utils/truncateText";

import {
  bloodTypeValues,
  getDietValues,
  getSmokingHabitsValues,
  getYesNoValues,
} from "@data/dropdownValues";
import { getGeneralConsultationSteps } from "@data/generalConsultationSteps";

import { MAX_PAGE_SIZE, PAGE_TITLE, ROLES } from "@constants/index";

import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useCreateConsultation } from "@hooks/mutations/consultation/useCreateConsultation";
import { useDeleteInjection } from "@hooks/mutations/consultation/useDeleteInjection";
import { useDeleteTreatmentPlan } from "@hooks/mutations/consultation/useDeleteTreatmentPlan";
import { useUpdateConsultation } from "@hooks/mutations/consultation/useUpdateConsultation";
import { useDeleteLab } from "@hooks/mutations/lab/useDeleteLab";
import { useDeleteMedication } from "@hooks/mutations/prescription/useDeleteMedication";
import { useEndTeleconsultation } from "@hooks/mutations/teleconsultation/useEndTeleconsultation";
import { useGetFollowUpAppointmentsForConsultation } from "@hooks/queries/appointment/useGetFollowUpAppointmentsForConsultation";
import { useGetClinicConsultationServices } from "@hooks/queries/clinic/useGetClinicConsultationServices";
import { useGetClinicLabs } from "@hooks/queries/clinic/useGetClinicLabs";
import { useGetInjectionsForConsultation } from "@hooks/queries/consultation/useGetInjectionsForConsultation";
import { useGetSingleConsultation } from "@hooks/queries/consultation/useGetSingleConsultation";
import { useGetTreatmentPlans } from "@hooks/queries/consultation/useGetTreatmentPlans";
import { useGetLabsForPatient } from "@hooks/queries/lab/useGetLabsForPatient";
import { useGetPatientAllergies } from "@hooks/queries/patient/useGetPatientAllergies";
import { useGetPatientMedicalHistory } from "@hooks/queries/patient/useGetPatientMedicalHistory";
import { useGetPatientSurgeries } from "@hooks/queries/patient/useGetPatientSurgeries";
import { useGetSinglePatient } from "@hooks/queries/patient/useGetSinglePatient";
import { useGetMedicationsForPrescription } from "@hooks/queries/prescription/useGetMedicationsForPrescription";
import { useGetPrescriptionForConsultation } from "@hooks/queries/prescription/useGetPrescriptionForConsultation";
import { useGetSickNotesForPatient } from "@hooks/queries/sickNote/useGetSickNotesForPatient";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";
import { useVideoCallThreshold } from "@hooks/useVideoCallThreshold";

import { useAuth } from "@contexts/auth";

import { Appointment } from "@customTypes/appointment";
import { Consultation, TreatmentPlan } from "@customTypes/consultation";
import { ConsultationServiceComboboxItem } from "@customTypes/index";
import { Injection } from "@customTypes/injection";
import { Laboratory } from "@customTypes/laboratory";
import { Medication } from "@customTypes/prescription";
import { SickNote } from "@customTypes/sickNote";
import { BasicUser } from "@customTypes/user";

import LocalUserLeaveCallSound from "@assets/sound/local-user-leave-call.wav";

import { AllergyCard } from "../Profile/components/AllergyCard";

import { AddFileModal } from "./components/AddFileModal";
import { AddTreatmentPlanModal } from "./components/AddTreatmentPlanModal";
import { EditFollowUpAppointmentModal } from "./components/EditFollowUpApptModal";
import { EditTreatmentPlanModal } from "./components/EditTreatmentPlanModal";
import { InjectionModal } from "./components/InjectionModal";
import { JoinCallWarningModal } from "./components/JoinCallWarningModal";
import { SickNoteModal } from "./components/SickNoteModal";
import { VideoCall } from "./components/VideoCall";
import { VideoCallFeedbackModal } from "./components/VideoCallFeedbackModal";
import { StyledGeneralConsultation } from "./GeneralConsultation.styled";


const TOTAL_STEPS = getGeneralConsultationSteps().length;

const MAX_FILENAME_LENGTH_AFTER_UPLOAD = 32 + 8; // Default + modification

export const GeneralConsultation = () => {
  const { t, i18n } = useTranslation();

  useDocumentTitle(`${t("general-consultation")} • ${PAGE_TITLE}`);
  const { id: userId, token, roles , can } = useAuth();
  const navigate = useNavigate();
  const { patientId, consultationId } = useParams();
  const { state, pathname } = useLocation();
  const [sessionStep, setSessionStep] = useSessionStorage({
    key: "gc-step",
    defaultValue: "0", //* Avoiding using 1 because there is an undesirable flash of the first step when the page loads
  });

  const [date, setDate] = useState<Date | null>();
  const [step, setStep] = useState(+sessionStep);
  const [moveToStep, setMoveToStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [consultationService, setConsultationService] =
    useState<ConsultationServiceComboboxItem | null>(null);
  const [supportStaff, setSupportStaff] = useState<ComboboxItem[]>([]);
  const [bloodType, setBloodType] = useState<string | null>(null);
  const [bloodDonor, setBloodDonor] = useState<string | null>(null);

  const [smokingHabits, setSmokingHabits] = useState<string | null>(null);
  const [diet, setDiet] = useState<string | null>(null);
  const [formData, setFormData] = useState({} as Partial<Consultation>);
  const [currentLabOrPrescId, setCurrentLabOrPrescId] = useState("");
  const [selectedLab, setSelectedLab] = useState<Partial<Laboratory>>({});
  const [selectedMedication, setSelectedMedication] = useState<Medication>(
    {} as Medication
  );
  const [shouldCreatePrescription, setShouldCreatePrescription] =
    useState(false);
  const [currentSickNote, setCurrentSickNote] = useState<SickNote | null>(null);
  const [currentTreatmentPlan, setCurrentTreatmentPlan] =
    useState<TreatmentPlan | null>(null);
  const [currentFollowUpAppointment, setCurrentFollowUpAppointment] =
    useState<Partial<Appointment> | null>(null);
  const [currentInjection, setCurrentInjection] = useState<Injection | null>(
    null
  );
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [procedureAndTestsTab, setProcedureAndTestsTab] = useState("analysis");
  const recommendationFieldRef = useRef<HTMLTextAreaElement>(null);
  const observationFieldRef = useRef<HTMLTextAreaElement>(null);
  const [isRecommendationFieldFocused, setIsRecommendationFieldFocused] =
    useState(false); // Need to use this because submit function is not capturing the ref properly to sets focus on it directly, and moveToStep doesn't work when I try useCallback()
  const [isObservationFieldFocused, setIsObservationFieldFocused] =
    useState(false);

  // For video calls
  const [isCalling, setIsCalling] = useState(false);
  const prevIsCalling = usePrevious(isCalling);
  const [callDuration, setCallDuration] = useState(0); // in seconds
  const isConnected = useIsConnected();
  const localUserLeaveCallAudioRef = useRef<HTMLAudioElement>(null);

  const [
    editLabModalOpened,
    { open: editLabModalOpen, close: editLabModalClose },
  ] = useDisclosure(false);
  const [
    addLabModalOpened,
    { open: addLabModalOpen, close: addLabModalClose },
  ] = useDisclosure(false);

  const [
    editMedicationModalOpened,
    { open: editMedicationModalOpen, close: editMedicationModalClose },
  ] = useDisclosure(false);
  const [
    addMedicationModalOpened,
    { open: addMedicationModalOpen, close: addMedicationModalClose },
  ] = useDisclosure(false);
  const [
    createAppointmentModalOpened,
    { open: createAppointmentModalOpen, close: createAppointmentModalClose },
  ] = useDisclosure(false);
  const [
    videoCallFeedbackModalOpened,
    { open: openVideoCallFeedbackModal, close: closeVideoCallFeedbackModal },
  ] = useDisclosure(false);
  const [
    joinCallWarningModalOpened,
    { open: openJoinCallWarningModal, close: closeJoinCallWarningModal },
  ] = useDisclosure(false);
  const [
    addFileModalOpened,
    { open: openAddFileModal, close: closeAddFileModal },
  ] = useDisclosure(false);
  const [
    addSickNoteModalOpened,
    { open: openAddSickNoteModal, close: closeAddSickNoteModal },
  ] = useDisclosure(false);
  const [
    editSickNoteModalOpened,
    { open: openEditSickNoteModal, close: closeEditSickNoteModal },
  ] = useDisclosure(false);
  const [
    addTreatmentPlanModalOpened,
    { open: openAddTreatmentPlanModal, close: closeAddTreatmentPlanModal },
  ] = useDisclosure(false);
  const [
    editTreatmentPlanModalOpened,
    { open: openEditTreatmentPlanModal, close: closeEditTreatmentPlanModal },
  ] = useDisclosure(false);
  const [
    editFollowUpAppointmentModalOpened,
    {
      open: openEditFollowUpAppointmentModal,
      close: closeEditFollowUpAppointmentModal,
    },
  ] = useDisclosure(false);
  const [
    injectionModalOpened,
    { open: openInjectionModal, close: closeInjectionModal },
  ] = useDisclosure(false);

  const {
    data: consultationData,
    isLoading: isConsultationLoading,
    isRefetching: isConsultationRefetching,
  } = useGetSingleConsultation({
    consultationId: consultationId ?? "",
    isEnabled: !!consultationId,
  });
  const { data: surgeriesData } = useGetPatientSurgeries({
    patientId: patientId as string,
  });
  const { data: medicalHistoryData } = useGetPatientMedicalHistory({
    patientId: patientId as string,
  });
  const { data: allergiesData } = useGetPatientAllergies({
    patientId: patientId as string,
  });
  const { data: profileData } = useGetSinglePatient({
    patientId: patientId as string,
  });
  const { data: consultationServicesData } = useGetClinicConsultationServices();
  const { data: supportStaffData, isLoading: isSupportStaffDataLoading } =
    useGetUsers({ permission: "consultations.edit" });
  const {
    mutate: createConsultation,
    isLoading: isCreateConsultationLoading,
    isSuccess: isCreateConsultationSuccess,
  } = useCreateConsultation();
  const { mutate: updateConsultation, isLoading: isUpdateConsultationLoading } =
    useUpdateConsultation();
  const {
    data: bioLabsData,
    isSuccess: isBioLabsSuccess,
    isLoading: isBioLabsLoading,
  } = useGetLabsForPatient({
    patientId: patientId as string,
    consultationId: consultationId ?? "",
    category: "bio_analysis",
    pagination: {
      page: 1,
      pageSize: 100,
    },
    isEnabled: !!consultationId,
  });
  const {
    data: imagingLabsData,
    isSuccess: isImagingLabsSuccess,
    isLoading: isImagingLabsLoading,
  } = useGetLabsForPatient({
    patientId: patientId as string,
    consultationId: consultationId ?? "",
    category: "imaging",
    pagination: {
      page: 1,
      pageSize: 100,
    },
    isEnabled: !!consultationId,
  });
  const { mutate: deleteLab, isLoading: isDeleteLabLoading } = useDeleteLab();
  const { data: clinicLabsData } = useGetClinicLabs();

  const {
    data: treatmentPlansData,
    isLoading: isTreatmentPlansLoading,
    isSuccess: isTreatmentPlansSuccess,
    isError: isTreatmentPlansError,
  } = useGetTreatmentPlans({
    patientId: patientId ?? "",
    consultationId: consultationId ?? "",
    isEnabled: !!formData?.id,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const {
    mutate: deleteTreatmentPlan,
    isLoading: isDeleteTreatmentPlanLoading,
  } = useDeleteTreatmentPlan();

  const {
    data: prescriptionData,
    isLoading: isPrescriptionLoading,
    isError: isPrescriptionError,
    isSuccess: isPrescriptionSuccess,
  } = useGetPrescriptionForConsultation({
    isEnabled: !!formData?.id,
    consultationId: formData?.id || "",
  });
  const {
    data: medicationsData,
    isLoading: isMedicationsLoading,
    isSuccess: isMedicationsSuccess,
  } = useGetMedicationsForPrescription({
    prescriptionId: prescriptionData?.id ?? "",
    isEnabled: !!prescriptionData,
    patientId: patientId ?? "",
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const { mutate: deleteMedication, isLoading: isDeleteMedicationLoading } =
    useDeleteMedication();
  const {
    data: followUpAppointmentsData,
    isLoading: isFollowUpAppointmentsLoading,
    isError: isFollowUpAppointmentsError,
    isSuccess: isFollowUpAppointmentsSuccess,
  } = useGetFollowUpAppointmentsForConsultation({
    isEnabled: !!consultationId,
    pagination: { page: 1, pageSize: 30 }, // Pagination might be added later
    consultationId: consultationId || "",
    fields: [
      "_id",
      "doctor_assigned",
      "created_at",
      "appointment_date",
      "category",
      "details",
    ],
  });
  const { data: sickNotesData } = useGetSickNotesForPatient({
    patientId: patientId ?? "",
    consultationId: consultationId ?? "",
  });
  const {
    data: injectionsData,
    isLoading: isInjectionsLoading,
    isError: isInjectionsError,
    isSuccess: isInjectionsSuccess,
  } = useGetInjectionsForConsultation({
    consultationId: consultationId ?? "",
    isEnabled: !!consultationId,
  });
  const { mutate: deleteInjection, isLoading: isDeleteInjectionLoading } =
    useDeleteInjection();

  const { mutate: updateAppointment, isLoading: isUpdateAppointmentLoading } =
    useUpdateAppointment();
  const { mutate: endTeleconsultation } = useEndTeleconsultation();
  const handleErrors = useQueryErrorHandling();

  useVideoCallThreshold({
    openVideoCallFeedbackModal,
    hasCallEnded: !!prevIsCalling && !isCalling,
    callDuration,
    appointmentId:
      state?.selectedConsultation?.appointmentId ||
      consultationData?.appointmentId ||
      "",
  });

  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);

  const handleDeleteTreatmentPlan = (plan: Partial<TreatmentPlan>) => {
    deleteTreatmentPlan({
      treatmentId: plan.id ?? "",
      patientId: patientId as string,
    });
  };

  const followUpAppointmentRows = useMemo(() => {
    return followUpAppointmentsData?.newData?.map((item, index) => {
      return (
        <>
          <Table.Tr key={index}>
            <Table.Td>
              <Stack gap={0}>
                <Text fz={14} lh={1.5}>
                  {formatDateToString(item.appointmentDate!)}
                </Text>
                <Text fz={13} c="dimmed" lh={1.5}>
                  {item.appointmentDate?.toLocaleTimeString(i18n.language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </Stack>
            </Table.Td>
            <Table.Td>
              <Stack gap={0}>
                <Text fz={14} lh={1.5}>
                  {formatDateToString(item.createdAt!)}
                </Text>
                <Text fz={13} c="dimmed" lh={1.5}>
                  {item.createdAt?.toLocaleTimeString(i18n.language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </Stack>
            </Table.Td>
            <Table.Td>{(item?.doctorAssigned as BasicUser).fullName}</Table.Td>
            <Table.Td w={"min-content"}>
              <Group gap={3} justify="end">
                <Tooltip label={"Edit appointment"} withArrow>
                  <PermissionGate permission="appointments.edit"><ActionIcon
                    variant="outline"
                    onClick={() => {
                      setCurrentFollowUpAppointment(item);
                      openEditFollowUpAppointmentModal();
                    }}>
                    <IconPencil size={16} stroke={1.5} />
                  </ActionIcon></PermissionGate>
                </Tooltip>
                <Tooltip label={"Cancel appointment"} withArrow>
                  <ActionIcon
                    onClick={() => {
                      const jsFormData = new FormData();
                      jsFormData.append("status", "cancelled");
                      updateAppointment({
                        appointmentId: item.id!,
                        formData: jsFormData,
                      });
                    }}
                    loading={isUpdateAppointmentLoading}
                    variant="outline"
                    color="red">
                    <IconX size={16} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Table.Td>
          </Table.Tr>
        </>
      );
    });
  }, [
    i18n.language,
    followUpAppointmentsData?.newData,
    isUpdateAppointmentLoading,
  ]);

  const treatmentPlanRows = useMemo(() => {
    return treatmentPlansData?.newData?.map((item, index) => (
      <Table.Tr key={index}>
        <Table.Td>{item.treatment?.label}</Table.Td>
        <Table.Td>{item.quantity}</Table.Td>
        <Table.Td>{item.supportStaff?.label}</Table.Td>
        <Table.Td>
          <Stack gap={0}>
            <Text fz={14} lh={1.5}>
              {formatDateToString(item.date)}
            </Text>
            <Text fz={13} c="dimmed" lh={1.5}>
              {item.date?.toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td w={"min-content"}>
          <Group gap={3} justify="end">
            <Tooltip label={"Edit treatment"} withArrow>
              <ActionIcon
                onClick={() => {
                  setCurrentTreatmentPlan(item);
                }}
                variant="outline"
                disabled={isDeleteTreatmentPlanLoading}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={"Delete treatment"} withArrow>
              <PermissionGate permission="treatments.delete"><ActionIcon
                loading={isDeleteTreatmentPlanLoading}
                variant="outline"
                color="red"
                onClick={() => {
                  handleDeleteTreatmentPlan(item);
                }}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [
    treatmentPlansData?.newData,
    isDeleteTreatmentPlanLoading,
    i18n.language,
    currentTreatmentPlan,
  ]);

  const injectionsRows = useMemo(() => {
    return injectionsData?.map((item, index) => (
      <Table.Tr key={index}>
        <Table.Td>{(item.service as ComboboxItem)?.label}</Table.Td>
        <Table.Td>
          <Stack gap={10}>
            {item.consumables?.map(consumable => (
              <Text fz={14} key={consumable.id}>
                {consumable?.name ?? ""}
              </Text>
            ))}
          </Stack>
        </Table.Td>
        <Table.Td>
          <Stack gap={10}>
            {item.consumables?.map(consumable => (
              <Text fz={14} key={consumable.id}>
                {consumable?.quantity ?? ""}
              </Text>
            ))}
          </Stack>
        </Table.Td>
        <Table.Td w={"min-content"}>
          <Group gap={3} justify="end">
            <Tooltip label={"Edit injection"} withArrow>
              <ActionIcon
                onClick={() => {
                  setCurrentInjection(item);
                  openInjectionModal();
                }}
                variant="outline"
                disabled={isDeleteInjectionLoading}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={"Delete injection"} withArrow>
              <PermissionGate permission="treatments.delete"><ActionIcon
                loading={isDeleteInjectionLoading}
                variant="outline"
                color="red"
                onClick={() =>
                  deleteInjection({
                    injectionId: item.id,
                    consultationId: consultationId ?? "",
                  })
                }>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [
    injectionsData,
    isInjectionsLoading,
    isDeleteInjectionLoading,
    i18n.language,
    currentInjection,
  ]);

  const handleFormSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (step === 1) {
      // if (state?.selectedConsultation || formData?.id) {
      if (formData?.id) {
        if (moveToStep) {
          setStep(moveToStep);
          setSessionStep(String(moveToStep));
          setMoveToStep(0);
        } else {
          setStep(step + 1);
          setSessionStep(String(step + 1));
        }
        return;
      }

      const jsFormData = new FormData();
      jsFormData.append("consultation_method", "standard");
      jsFormData.append("consultation_type", "General");
      jsFormData.append("status", "incomplete");
      jsFormData.append("visibility", "unreleased");
      jsFormData.append("patient_id", patientId as string);

      jsFormData.append(
        "consultation_service",
        consultationService?.value ?? ""
      );
      jsFormData.append(
        "date",
        date ? combineDateWithCurrentTime(date).toISOString() : ""
      );
      jsFormData.append("physician", userId ?? "");

      jsFormData.append("aid", supportStaff.map(st => st.value).join(","));
      jsFormData.append("location", formData?.location ?? "");
      jsFormData.append("reason", formData?.reason ?? "");
      jsFormData.append("blood_type", formData?.bloodType ?? "");
      jsFormData.append("donor", formData?.bloodDonor ?? "");
      jsFormData.append("height", formData?.height ?? "");
      jsFormData.append("weight", formData?.weight ?? "");
      jsFormData.append("occupation", formData?.occupation ?? "");
      jsFormData.append("alcohol_consumption", formData?.drinkingHabits ?? "");
      jsFormData.append("diet", formData?.diet ?? "");
      jsFormData.append(
        "exercising_frequency",
        formData?.exerciseFrequency ?? ""
      );
      jsFormData.append("smoking_habits", formData?.smokingHabits ?? "");
      jsFormData.append(
        "cancellation_reason",
        formData?.cancellationReason ?? ""
      );
      jsFormData.append("recommendation", formData?.recommendation ?? "");

      const id = showLoadingNotification({
        title: "getting-ready",
        message: "starting-consultation",
      });
      createConsultation(jsFormData, {
        onSuccess: (res: any) => {
          showSuccessNotification({
            id,
            title: "success",
            message: "start-consultation-success",
          });
          navigate(`${pathname}/${res.data._id}`, {
            replace: true,
          });

          if (moveToStep) {
            setStep(moveToStep);
            setSessionStep(String(moveToStep));
            setMoveToStep(0);
          } else {
            setStep(step + 1);
            setSessionStep(String(step + 1));
          }

          setFormData({
            ...formData,
            id: res.data._id,
            cId: res.data.c_id,
          });
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      });
      console.log("Submitting and moving to medical form");
    } else if (step === TOTAL_STEPS) {
      // Finalizing consultation

      // Check if required fields are filled first
      if (!formData.symptoms?.length || !formData?.recommendation?.length) {
        showErrorNotification({
          title: "oops",
          message: "required-fields-error",
        });
        if (!formData.symptoms?.length) {
          // observationFieldRef?.current?.focus();
          setIsObservationFieldFocused(true);
          setMoveToStep(4);
          console.log("in here 1");
          return;
        }
        if (!formData.recommendation?.length) {
          setIsRecommendationFieldFocused(true);
          // recommendationFieldRef?.current?.focus();
          setMoveToStep(6);
          console.log("in here 2");
          return;
        }
      }

      const jsFormData = new FormData();
      // jsFormData.append("physician", userId);
      uploadedFiles?.length &&
        uploadedFiles.forEach(file => {
          jsFormData.append(`files`, file);
        });

      diagnoses?.length &&
        diagnoses.forEach(diagnosis => {
          jsFormData.append("diagnosis", diagnosis);
        });
      jsFormData.append(
        "date",
        date ? combineDateWithCurrentTime(date).toISOString() : ""
      );
      supportStaff?.length &&
        jsFormData.append("aid", supportStaff.map(st => st.value).join(","));
      jsFormData.append(
        "consultation_service",
        consultationService?.value ?? ""
      );

      jsFormData.append("status", "complete");
      jsFormData.append("blood_pressure", formData?.bloodPressure ?? "");
      jsFormData.append("pulse_rate", formData?.pulseRate ?? "");
      jsFormData.append("respiratory_rate", formData?.respiratoryRate ?? "");
      jsFormData.append("temperature", formData?.temperature ?? "");
      jsFormData.append("oxygen_saturation", formData?.oxygenSaturation ?? "");
      jsFormData.append("symptoms", formData?.symptoms ?? "");
      jsFormData.append("examination", formData?.examination ?? "");
      jsFormData.append("follow_up", formData?.followUp ?? "");
      jsFormData.append("treatment", formData?.treatment ?? "");
      jsFormData.append(
        "cancellation_reason",
        formData?.cancellationReason ?? ""
      );
      jsFormData.append("recommendation", formData?.recommendation ?? "");

      const id = showLoadingNotification({});
      updateConsultation(
        {
          consultationId: formData?.id as string,
          formData: jsFormData,
        },
        {
          onSuccess: () => {
            showSuccessNotification({
              id,
              title: "success",
              message: "finalize-consultation-success",
            });
            navigate("..", {
              relative: "route",
              state: { isFinalizedConsultation: true },
            });
          },
          onError: (res: any) => {
            handleErrors(res, "mutation", id);
          },
        }
      );
      console.log("Finalizing consultation");
    } else if (step < TOTAL_STEPS) {
      if (moveToStep) {
        setStep(moveToStep);
        setSessionStep(String(moveToStep));
        setMoveToStep(0);
      } else {
        setStep(step + 1);
        setSessionStep(String(step + 1));
      }
    }
  };

  const saveForm = () => {
    const jsFormData = new FormData();
    // jsFormData.append("physician", userId);
    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });

    diagnoses.length &&
      diagnoses.forEach(diagnosis => {
        jsFormData.append("diagnosis", diagnosis);
      });

    jsFormData.append(
      "date",
      date ? combineDateWithCurrentTime(date).toISOString() : ""
    );
    jsFormData.append(
      "aid",
      supportStaff?.length ? supportStaff.map(st => st.value).join(",") : `""`
    );
    jsFormData.append("consultation_service", consultationService?.value ?? "");
    jsFormData.append("location", formData?.location ?? "");
    jsFormData.append("reason", formData?.reason ?? "");

    jsFormData.append("blood_type", formData?.bloodType ?? "");
    jsFormData.append("donor", formData?.bloodDonor ?? "");
    jsFormData.append("height", formData?.height ?? "");
    jsFormData.append("weight", formData?.weight ?? "");
    jsFormData.append("occupation", formData?.occupation ?? "");
    jsFormData.append("alcohol_consumption", formData?.drinkingHabits ?? "");
    jsFormData.append("diet", formData?.diet ?? "");
    jsFormData.append(
      "exercising_frequency",
      formData?.exerciseFrequency ?? ""
    );
    jsFormData.append("smoking_habits", formData?.smokingHabits ?? "");

    jsFormData.append("blood_pressure", formData?.bloodPressure ?? "");
    jsFormData.append("pulse_rate", formData?.pulseRate ?? "");
    jsFormData.append("respiratory_rate", formData?.respiratoryRate ?? "");
    jsFormData.append("temperature", formData?.temperature ?? "");
    jsFormData.append("oxygen_saturation", formData?.oxygenSaturation ?? "");
    jsFormData.append("symptoms", formData?.symptoms ?? "");
    jsFormData.append("examination", formData?.examination ?? "");
    jsFormData.append("follow_up", formData?.followUp ?? "");
    jsFormData.append("treatment", formData?.treatment ?? "");
    jsFormData.append(
      "cancellation_reason",
      formData?.cancellationReason ?? ""
    );
    jsFormData.append("recommendation", formData?.recommendation ?? "");

    const id = showLoadingNotification({});
    updateConsultation(
      {
        consultationId: formData?.id as string,
        formData: jsFormData,
      },
      {
        onSuccess: () => {
          // state.selectedConsultation = null;
          showSuccessNotification({
            id,
            title: "success",
            message: "update-consultation-success",
          });
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  const getButtonText = () => {
    if (step === TOTAL_STEPS) {
      return t("finalize-consultation");
    } else if (step === 1) {
      if (
        // state?.selectedConsultation ||
        isCreateConsultationSuccess ||
        consultationData
      ) {
        return t("next");
      } else return t("start-consultation");
    } else return t("next");
  };

  const getBMI = useCallback(() => {
    return calculateBMI(formData?.height as string, formData?.weight as string);
  }, [formData.height, formData.weight]);

  // useLogger("treatmentPlans", [{ treatmentPlans, treatmentPlanStates }]);

  useEffect(() => {
    if (bloodType) {
      setFormData(prev => ({ ...prev, bloodType }));
    }
  }, [bloodType]);

  useEffect(() => {
    if (bloodDonor) {
      setFormData(prev => ({ ...prev, bloodDonor }));
    }
  }, [bloodDonor]);

  useEffect(() => {
    if (smokingHabits) {
      setFormData(prevData => ({
        ...prevData,
        smokingHabits,
      }));
    }
  }, [smokingHabits]);

  useEffect(() => {
    if (diet) {
      setFormData(prevData => ({
        ...prevData,
        diet,
      }));
    }
  }, [diet]);

  useEffect(() => {
    if (consultationData) {
      import.meta.env.DEV &&
        console.log("Get consultation data:", consultationData);
      setFormData(prev => ({
        ...prev,
        ...consultationData,
      }));
      setUploadedFiles([]);
      setDate(new Date(consultationData.date));
      setSupportStaff([
        ...consultationData.supportStaff.map((item: BasicUser) => ({
          label: item.fullName,
          value: item.id,
        })),
      ]);
      setDiagnoses(
        consultationData.diagnosis ? [...consultationData.diagnosis] : []
      );
      if (consultationData.consultationService?.id) {
        setConsultationService({
          label: consultationData.consultationService?.name,
          value: consultationData.consultationService?.id,
          category: consultationData.consultationService?.category ?? "",
        });
      } else {
        showErrorNotification({
          title: "oops",
          message: "consultation-service-error",
        });
      }
    }

    if (profileData && !consultationData) {
      const {
        drinkingHabits,
        bloodDonor,
        bloodType,
        diet,
        exerciseFrequency,
        height,
        weight,
        occupation,
        smokingHabits,
      } = profileData;
      setFormData(prev => ({
        ...prev,
        drinkingHabits,
        bloodDonor,
        bloodType,
        diet,
        exerciseFrequency,
        height,
        weight,
        occupation,
        smokingHabits,
        //* not part of patient data, but needed here
        consultationType: "General",
      }));
    }
  }, [profileData, consultationData]);

  useLayoutEffect(() => {
    if (+sessionStep > 1 && formData?.id) {
      setStep(+sessionStep);
    } else {
      setStep(1);
    }
  }, [sessionStep]);

  useEffect(() => {
    if (moveToStep) {
      if (step === 1) {
        if (
          !consultationService?.value ||
          !date ||
          !formData?.location ||
          !formData?.reason
        ) {
          showErrorNotification({ message: "required-fields-error" });
          setMoveToStep(0); // Resetting to 0 so that clicking on the same step or any other step will retrigger this effect
        } else handleFormSubmit();
      } else {
        setStep(moveToStep);
        setSessionStep(String(moveToStep));
        setMoveToStep(0);
      }
    }
  }, [moveToStep]);

  // useLogger("General Consultation form", [{ formData }]);

  useEffect(() => {
    // Call initiated
    // if (!prevIsCalling && isCalling) {
    //   console.log("Call just started");
    // }

    // Call just ended
    if (prevIsCalling && !isCalling && localUserLeaveCallAudioRef?.current) {
      localUserLeaveCallAudioRef.current.volume = 0.5;
      localUserLeaveCallAudioRef.current.play();
      endTeleconsultation(consultationId ?? "");
      import.meta.env.DEV && console.log("Call just ended");
    }
  }, [isCalling]);

  useEffect(() => {
    if (currentSickNote?.startDate) {
      openEditSickNoteModal();
    }
  }, [currentSickNote]);

  useEffect(() => {
    if (currentTreatmentPlan?.id) {
      openEditTreatmentPlanModal();
    }
  }, [currentTreatmentPlan]);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledGeneralConsultation>
          <div className={`header ${isCalling ? "sticky" : ""}`}>
            <div className="back-btn-wrapper">
              <Link to=".." className="back-btn">
                <HiArrowLeft /> <span>{t("back")}</span>
              </Link>
              <Group gap={7}>
                <h2>{t("general-consultation")}</h2>
                {state?.selectedConsultation?.cId || consultationData?.cId ? (
                  <Badge
                    c="accent.4"
                    bg="accent.0"
                    ff={"var(--font-secondary)"}>
                    <Text span inherit fw={600}>
                      {state?.selectedConsultation?.cId || formData.cId}
                    </Text>
                  </Badge>
                ) : null}
                {!consultationId ? (
                  <Badge c="accent.4" bg="accent.0">
                    {t("new")}
                  </Badge>
                ) : null}
              </Group>
            </div>

            {consultationId && can("consultations.release") && (
              <Group gap={"xs"}>
                <VideoCall
                  consultationId={consultationId ?? ""}
                  setCallDuration={setCallDuration}
                  setIsCalling={setIsCalling}
                  isCalling={isCalling}
                  patientId={patientId as string}
                  appointmentId={
                    state?.selectedConsultation?.appointmentId ||
                    consultationData?.appointmentId
                  }
                  cId={
                    !consultationId
                      ? t("new")
                      : state?.selectedConsultation?.cId || formData.cId
                  }
                />
                <VideoCallFeedbackModal
                  opened={videoCallFeedbackModalOpened}
                  close={closeVideoCallFeedbackModal}
                />
                <JoinCallWarningModal
                  opened={joinCallWarningModalOpened}
                  close={closeJoinCallWarningModal}
                  setIsCalling={setIsCalling}
                  consultationId={consultationId}
                />
                <audio
                  ref={localUserLeaveCallAudioRef}
                  src={LocalUserLeaveCallSound}>
                  <track
                    kind="captions"
                    src=""
                    srcLang="en"
                    label="English"
                    default
                  />
                </audio>

                {!isConnected && (
                  <>
                    <PermissionGate permission="communication.call"><Button
                      color="accent.4"
                      leftSection={<IconVideo size={18} stroke={2} />}
                      disabled={isCalling}
                      onClick={() => openJoinCallWarningModal()}>
                      {isCalling ? t("joining-call") : t("start-call")}
                    </Button></PermissionGate>
                    {isCalling && (
                      <ActionIcon
                        size={"lg"}
                        variant="filled"
                        onClick={() => {
                          setIsCalling(false);
                        }}
                        color={"red"}>
                        <IconPhone style={{ rotate: "135deg" }} size={20} />
                      </ActionIcon>
                    )}
                  </>
                )}
              </Group>
            )}
          </div>

          <div className="content">
            <LoadingOverlay
              visible={
                (isConsultationLoading || isConsultationRefetching) &&
                !!consultationId
              }
              zIndex={1000}
              loaderProps={{
                color: "accent.4",
                ml: "calc(var(--sidebar-width) * -1)",
                mt: "calc(var(--patient-navbar-height) * -1)",
              }}
              overlayProps={{ radius: "sm", blur: 2 }}
            />
            <ul className="stepper">
              {getGeneralConsultationSteps().map(
                ({ title, position, hasRequiredFields }) => (
                  <li key={position} className={`stepper-item`}>
                    <button
                      className={` ${step === position ? "active" : ""} ${
                        step > position ? "completed" : ""
                      }`}
                      onClick={() => {
                        setMoveToStep(position);
                      }}>
                      <div className="icon-wrapper">
                        {step <= position ? (
                          <span>{position}</span>
                        ) : (
                          <BsCheckLg />
                        )}
                      </div>
                      <span>
                        {title}
                        {hasRequiredFields && <sup>*</sup>}
                      </span>
                    </button>
                  </li>
                )
              )}
            </ul>

            <form className="form" onSubmit={handleFormSubmit}>
              {step === 1 ? (
                <div className="form-step">
                  <div className="form-group">
                    <div className="form-field">
                      <label htmlFor="location">
                        {t("location")} <sup>*</sup>
                      </label>
                      <input
                        type="text"
                        name="location"
                        id="location"
                        value={formData?.location ?? ""}
                        onChange={e => handleOnChange(e, setFormData, formData)}
                      />
                    </div>
                    <div className="form-field">
                      <DatePickerInput
                        label={t("date")}
                        value={date}
                        onChange={setDate}
                        leftSection={<IconCalendar size={16} stroke={1.5} />}
                        withAsterisk
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="form-field">
                      <ConsultationServiceSelect
                        label={t("consultation-service")}
                        placeholder={t("select-service")}
                        data={consultationServicesData || []}
                        value={consultationService}
                        setValue={val => {
                          setConsultationService(
                            val as ConsultationServiceComboboxItem
                          );
                        }}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <MultiSelectWithSearch
                        label={t("support-staff")}
                        placeholder={t("select-support-staff")}
                        data={supportStaffComboboxItems ?? []}
                        isLoading={isSupportStaffDataLoading}
                        value={supportStaff}
                        setValue={setSupportStaff}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="reason">
                      {t("reason-for-service")} <sup>*</sup>
                    </label>
                    <textarea
                      rows={5}
                      placeholder={t("type-reason")}
                      id="reason"
                      name="reason"
                      value={formData?.reason ?? ""}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="cancellationReason">
                      {t("reason-for-cancellation")}
                    </label>
                    <textarea
                      rows={5}
                      placeholder={t("type-reason")}
                      id="cancellationReason"
                      name="cancellationReason"
                      value={formData?.cancellationReason ?? ""}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                    />
                    <div className="reason-templates">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            cancellationReason: `${prev.cancellationReason}\n\nCancelled - Patient no show`,
                          }))
                        }>
                        Cancelled - Patient no show
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            cancellationReason: `${prev.cancellationReason}\n\nCancelled - Doctor no show`,
                          }))
                        }>
                        Cancelled - Doctor no show
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            cancellationReason: `${prev.cancellationReason}\n\nCancelled - Unpaid bill`,
                          }))
                        }>
                        Cancelled - Unpaid bill
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            cancellationReason: `${prev.cancellationReason}\n\nPostponed - Patient request`,
                          }))
                        }>
                        Postponed - Patient request
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            cancellationReason: `${prev.cancellationReason}\n\nPostponed - Doctor request`,
                          }))
                        }>
                        Postponed - Doctor request
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {step === 2 ? (
                <Tabs
                  defaultValue={"histories"}
                  styles={{
                    panel: {
                      marginTop: "2rem",
                    },
                  }}>
                  <Tabs.List>
                    <Tabs.Tab value="histories">{t("histories")}</Tabs.Tab>
                    <Tabs.Tab value="lifestyle">{t("lifestyle")}</Tabs.Tab>
                    <Tabs.Tab value="medical-info">
                      {t("medical-info")}
                    </Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="lifestyle">
                    <div className="form-step">
                      <div className="form-group">
                        <div className="form-field">
                          <Select
                            label={t("blood-type")}
                            placeholder={t("select-blood-type")}
                            data={bloodTypeValues}
                            value={formData?.bloodType}
                            onChange={setBloodType}
                          />
                        </div>

                        <div className="form-field">
                          <Select
                            label={t("is-patient-donor")}
                            placeholder={t("select-option")}
                            data={getYesNoValues()}
                            value={formData?.bloodDonor}
                            onChange={setBloodDonor}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <div className="form-field">
                          <NumberInput
                            label={t("height")}
                            placeholder={t("eg", { example: 273 })}
                            name="height"
                            value={formData?.height ?? ""}
                            onChangeCapture={e =>
                              handleOnChange(e, setFormData, formData)
                            }
                          />
                        </div>
                        <div className="form-field">
                          <NumberInput
                            label={t("weight")}
                            name="weight"
                            placeholder={t("eg", { example: 70 })}
                            value={formData?.weight ?? ""}
                            onChangeCapture={e =>
                              handleOnChange(e, setFormData, formData)
                            }
                          />
                        </div>
                      </div>
                      <TextInput
                        w={"calc(50% - 1rem)"}
                        label={t("bmi")}
                        value={getBMI()}
                        disabled
                        description={t("auto-update-bmi")}
                      />
                    </div>
                  </Tabs.Panel>
                  <Tabs.Panel value="medical-info">
                    <div className="form-step">
                      <div className="form-group">
                        <div className="form-field">
                          <label htmlFor="occupation">{t("occupation")}</label>
                          <input
                            type="text"
                            placeholder={t("eg", { example: t("doctor") })}
                            id="occupation"
                            name="occupation"
                            value={formData?.occupation ?? ""}
                            onChange={e =>
                              handleOnChange(e, setFormData, formData)
                            }
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="drinkingHabits">
                            {t("alcohol-consumption")}
                          </label>
                          <input
                            type="text"
                            // placeholder="Eg: 3 units/week"
                            id="drinkingHabits"
                            name="drinkingHabits"
                            value={formData?.drinkingHabits ?? ""}
                            onChange={e =>
                              handleOnChange(e, setFormData, formData)
                            }
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <div className="form-field">
                          <Select
                            label={t("diet")}
                            placeholder={t("select-option")}
                            data={getDietValues()}
                            value={formData?.diet}
                            onChange={setDiet}
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="exercise">
                            {t("exercise-frequency")}
                          </label>
                          <input
                            type="text"
                            id="exercise"
                            name="exerciseFrequency"
                            value={formData?.exerciseFrequency ?? ""}
                            onChange={e =>
                              handleOnChange(e, setFormData, formData)
                            }
                          />
                        </div>
                      </div>

                      <div className="form-field half">
                        <Select
                          label={t("smoking-habits")}
                          placeholder={t("select-option")}
                          data={getSmokingHabitsValues()}
                          value={formData?.smokingHabits}
                          onChange={setSmokingHabits}
                        />
                      </div>
                    </div>
                  </Tabs.Panel>
                  <Tabs.Panel value="histories">
                    <div className="form-step">
                      <Group align="start" gap="md">
                        <MedicalHistoryCard
                          medicalHistory={medicalHistoryData ?? []}
                        />
                        <SurgicalHistoryCard surgeries={surgeriesData ?? []} />
                        <AllergyCard allergies={allergiesData ?? []} />
                      </Group>
                    </div>
                  </Tabs.Panel>
                </Tabs>
              ) : null}

              {step === 3 ? (
                <div className="form-step">
                  <div className="form-group">
                    <div className="form-field">
                      <label htmlFor="bp-1">{t("blood-pressure")}</label>
                      <input
                        id="bp-1"
                        type="text"
                        placeholder={t("eg", { example: "120/80" })}
                        name="bloodPressure"
                        value={formData?.bloodPressure ?? ""}
                        onChange={e => handleOnChange(e, setFormData, formData)}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="hr-1">{t("heart-rate")} (bpm)</label>
                      {/* <div className="input-wrapper">
                          <input type="text" placeholder="Eg: 170" id="hr-1" />
                          <span>/</span>
                          <input type="text" placeholder="Eg: 10" id="hr-2" />
                        </div> */}
                      <input
                        type="text"
                        id="hr-1"
                        name="pulseRate"
                        placeholder={t("eg", { example: 80 })}
                        value={formData?.pulseRate ?? ""}
                        onChange={e => handleOnChange(e, setFormData, formData)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="form-field">
                      <label htmlFor="rr-1">
                        {t("respiratory-rate")} (bpm)
                      </label>
                      <input
                        type="text"
                        id="rr-1"
                        name="respiratoryRate"
                        value={formData?.respiratoryRate ?? ""}
                        placeholder={t("eg", { example: 15 })}
                        onChange={e => handleOnChange(e, setFormData, formData)}
                      />
                    </div>

                    <div className="form-field">
                      <label htmlFor="temperature">
                        {t("temperature")} (°C)
                      </label>
                      <input
                        type="text"
                        placeholder={t("eg", { example: 37.2 })}
                        id="temperature"
                        name="temperature"
                        value={formData?.temperature ?? ""}
                        onChange={e => handleOnChange(e, setFormData, formData)}
                      />
                    </div>
                  </div>

                  <div className="form-field half">
                    <label htmlFor="oxygen">{t("oxygen-saturation")}(%)</label>
                    <input
                      type="text"
                      placeholder={t("eg", { example: 98 })}
                      id="oxygen"
                      name="oxygenSaturation"
                      value={formData?.oxygenSaturation ?? ""}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                    />
                  </div>
                </div>
              ) : null}

              {step === 4 ? (
                <div className="form-step">
                  <div className="form-field">
                    <label htmlFor="symptoms">
                      {t("health-professionals-observation")} <sup>*</sup>
                    </label>
                    <textarea
                      id="symptoms"
                      rows={10}
                      name="symptoms"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus={isObservationFieldFocused}
                      ref={observationFieldRef}
                      value={formData?.symptoms ?? ""}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                    />
                  </div>
                </div>
              ) : null}

              {step === 5 ? (
                <>
                  <AddLabModal
                    category={procedureAndTestsTab}
                    opened={addLabModalOpened}
                    close={addLabModalClose}
                    preConsultationComboboxItem={{
                      label: formData?.cId ?? "",
                      value: formData?.id ?? "",
                      date: `${formatDateToString(date as Date)}, ${(
                        date as Date
                      ).toLocaleTimeString(i18n.language, {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}`,
                    }}
                  />
                  <LabEditModal
                    opened={editLabModalOpened}
                    close={editLabModalClose}
                    lab={selectedLab}
                    labTypes={clinicLabsData || []}
                  />
                  <Tabs
                    value={procedureAndTestsTab}
                    onChange={val => setProcedureAndTestsTab(val as string)}
                    styles={{
                      panel: {
                        marginTop: "2rem",
                      },
                    }}>
                    <Tabs.List>
                      <Tabs.Tab value="analysis">{t("analysis")}</Tabs.Tab>
                      <Tabs.Tab value="imaging">{t("imaging")}</Tabs.Tab>
                      <Tabs.Tab value="injections">
                        {t("injections")}
                      </Tabs.Tab>
                    </Tabs.List>

                  <Tabs.Panel value="analysis">
                    <div className="form-step">
                      {isBioLabsLoading ? <Loader size={18} /> : null}

                      {!isBioLabsLoading && !bioLabsData?.newData?.length ? (
                        <Stack gap={"xs"}>
                          <Text fz={14} c="dimmed" lh={1.5}>
                            {t("consultation-labs-empty")}
                          </Text>
                          <PermissionGate permission="labs.create"><Button
                            variant="outline"
                            mt="sm"
                            w="fit-content"
                            onClick={() => {
                              addLabModalOpen();
                            }}
                            leftSection={<IconPlus size={18} />}>
                            {t("add-lab")}
                          </Button></PermissionGate>
                        </Stack>
                      ) : null}

                      {bioLabsData?.newData?.length && isBioLabsSuccess ? (
                        <>
                          <Stack gap={"xs"}>
                            <Text fz={14} c="dimmed" lh={1.5}>
                              {t("consultation-add-labs")}
                            </Text>
                            <PermissionGate permission="labs.create"><Button
                              mt={"sm"}
                              variant="outline"
                              w="fit-content"
                              onClick={() => {
                                addLabModalOpen();
                              }}
                              leftSection={<IconPlus size={18} />}>
                              {t("add-lab")}
                            </Button></PermissionGate>
                          </Stack>
                          <ul className="table">
                            <li className="table-header">
                              <p>{t("type")}</p>
                              <p>{t("reason")}</p>
                              <span></span>
                            </li>
                            {bioLabsData?.newData?.map(lab => (
                              <li className="table-row" key={lab.id}>
                                <p>
                                  {lab.files?.length ? (
                                    <GrAttachment
                                      size={14}
                                      color="var(--clr-accent)"
                                    />
                                  ) : null}
                                  &nbsp;{truncateWithEllipsis(lab.labType, 25)}
                                </p>
                                <p>
                                  {truncateWithEllipsis(lab.reason, 50) ||
                                    "N/A"}
                                </p>
                                <span>
                                  <Group gap="0.25rem">
                                    <Tooltip label={t("edit-lab")} withArrow>
                                      <ActionIcon
                                        disabled={
                                          isDeleteLabLoading &&
                                          lab.id === currentLabOrPrescId
                                        }
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedLab(lab);
                                          editLabModalOpen();
                                        }}>
                                        <IconPencil size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t("delete-lab")} withArrow>
                                      <PermissionGate permission={["labs.delete", "labs.archive"]}><ActionIcon
                                        color="red"
                                        loading={
                                          isDeleteLabLoading &&
                                          lab.id === currentLabOrPrescId
                                        }
                                        variant="outline"
                                        onClick={() => {
                                          const labToDelete = lab.labType;
                                          setCurrentLabOrPrescId(lab.id);
                                          openDeleteModal({
                                            textNode: (
                                              <Text fz={"sm"}>
                                                <Trans
                                                  i18nKey={"delete-lab-message"}
                                                  values={{ labToDelete }}
                                                  components={{
                                                    bold: (
                                                      <Text
                                                        fz={"inherit"}
                                                        fw={500}
                                                        lh={1.5}
                                                        span
                                                      />
                                                    ),
                                                  }}
                                                />
                                              </Text>
                                            ),
                                            onConfirm: () => {
                                              // loading notification is unnecessary here, but handleErrors() has been moved away from useMutation()
                                              const id =
                                                showLoadingNotification({});
                                              deleteLab(
                                                {
                                                  labId: lab.id,
                                                  patientId: lab.patientId,
                                                },
                                                {
                                                  onSuccess: () => {
                                                    showSuccessNotification({
                                                      id,
                                                      title: "success",
                                                      message:
                                                        "delete-lab-success",
                                                    });
                                                  },
                                                  onError: (res: any) => {
                                                    handleErrors(
                                                      res,
                                                      "mutation",
                                                      id
                                                    );
                                                  },
                                                }
                                              );
                                            },
                                          });
                                        }}>
                                        <IconTrash size={16} />
                                      </ActionIcon></PermissionGate>
                                    </Tooltip>
                                  </Group>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </div>
                  </Tabs.Panel>

                  <Tabs.Panel value="imaging">
                    <div className="form-step">
                      {isImagingLabsLoading ? <Loader size={18} /> : null}

                      {!isImagingLabsLoading &&
                      !imagingLabsData?.newData?.length ? (
                        <Stack gap={"xs"}>
                          <Text fz={14} c="dimmed" lh={1.5}>
                            {t("consultation-labs-empty")}
                          </Text>
                          <PermissionGate permission="labs.create"><Button
                            variant="outline"
                            mt="sm"
                            w="fit-content"
                            onClick={() => {
                              addLabModalOpen();
                            }}
                            leftSection={<IconPlus size={18} />}>
                            {t("add-lab")}
                          </Button></PermissionGate>
                        </Stack>
                      ) : null}

                      {imagingLabsData?.newData?.length &&
                      isImagingLabsSuccess ? (
                        <>
                          <Stack gap={"xs"}>
                            <Text fz={14} c="dimmed" lh={1.5}>
                              {t("consultation-add-labs")}
                            </Text>
                            <PermissionGate permission="labs.create"><Button
                              mt={"sm"}
                              variant="outline"
                              w="fit-content"
                              onClick={() => {
                                addLabModalOpen();
                              }}
                              leftSection={<IconPlus size={18} />}>
                              {t("add-lab")}
                            </Button></PermissionGate>
                          </Stack>
                          <ul className="table">
                            <li className="table-header">
                              <p>{t("type")}</p>
                              <p>{t("reason")}</p>
                              <span></span>
                            </li>

                            {imagingLabsData?.newData?.map(lab => (
                              <li className="table-row" key={lab.id}>
                                <p>
                                  {lab.files?.length ? (
                                    <GrAttachment
                                      size={14}
                                      color="var(--clr-accent)"
                                    />
                                  ) : null}
                                  &nbsp;{truncateWithEllipsis(lab.labType, 25)}
                                </p>
                                <p>
                                  {truncateWithEllipsis(lab.reason, 50) ||
                                    "N/A"}
                                </p>
                                <span>
                                  <Group gap="0.25rem">
                                    <Tooltip label={t("edit-lab")} withArrow>
                                      <ActionIcon
                                        disabled={
                                          isDeleteLabLoading &&
                                          lab.id === currentLabOrPrescId
                                        }
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedLab(lab);
                                          editLabModalOpen();
                                        }}>
                                        <IconPencil size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t("delete-lab")} withArrow>
                                      <PermissionGate permission={["labs.delete", "labs.archive"]}><ActionIcon
                                        color="red"
                                        loading={
                                          isDeleteLabLoading &&
                                          lab.id === currentLabOrPrescId
                                        }
                                        variant="outline"
                                        onClick={() => {
                                          const labToDelete = lab.labType;
                                          setCurrentLabOrPrescId(lab.id);
                                          openDeleteModal({
                                            textNode: (
                                              <Text fz={"sm"}>
                                                <Trans
                                                  i18nKey={"delete-lab-message"}
                                                  values={{
                                                    labToDelete,
                                                  }}
                                                  components={{
                                                    bold: (
                                                      <Text
                                                        fz={"inherit"}
                                                        fw={500}
                                                        lh={1.5}
                                                        span
                                                      />
                                                    ),
                                                  }}
                                                />
                                              </Text>
                                            ),
                                            onConfirm: () => {
                                              // loading notification is unnecessary here, but handleErrors() has been moved away from useMutation()
                                              const id =
                                                showLoadingNotification({});
                                              deleteLab(
                                                {
                                                  labId: lab.id,
                                                  patientId: lab.patientId,
                                                },
                                                {
                                                  onSuccess: () => {
                                                    showSuccessNotification({
                                                      id,
                                                      title: "success",
                                                      message:
                                                        "delete-lab-success",
                                                    });
                                                  },
                                                  onError: (res: any) => {
                                                    handleErrors(
                                                      res,
                                                      "mutation",
                                                      id
                                                    );
                                                  },
                                                }
                                              );
                                            },
                                          });
                                        }}>
                                        <IconTrash size={16} />
                                      </ActionIcon></PermissionGate>
                                    </Tooltip>
                                  </Group>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </div>
                  </Tabs.Panel>

                  <Tabs.Panel value="injections">
                    <div className="form-step">
                      <InjectionModal
                        opened={injectionModalOpened}
                        close={closeInjectionModal}
                        injection={currentInjection}
                      />
                      {isInjectionsLoading ? <Loader size={18} /> : null}

                      {!isInjectionsLoading && !injectionsData?.length ? (
                        <Stack gap={"xs"}>
                          <Text fz={14} c="dimmed" lh={1.5}>
                            {t("injections-empty")}
                          </Text>
                          <Button
                            variant="outline"
                            mt="sm"
                            w="fit-content"
                            onClick={() => {
                              openInjectionModal();
                            }}
                            leftSection={<IconPlus size={18} />}>
                            {t("add-injection")}
                          </Button>
                        </Stack>
                      ) : null}

                      {injectionsData?.length && isInjectionsSuccess ? (
                        <>
                          <Stack gap={"xs"}>
                            <Text fz={14} c="dimmed" lh={1.5}>
                              {t("consultation-add-injections")}
                            </Text>
                            <Button
                              mt={"sm"}
                              variant="outline"
                              w="fit-content"
                              onClick={() => {
                                setCurrentInjection(null);
                                openInjectionModal();
                              }}
                              leftSection={<IconPlus size={18} />}>
                              {t("add-injection")}
                            </Button>
                          </Stack>

                          <Table
                            mt={"2rem"}
                            withRowBorders={true}
                            withTableBorder
                            highlightOnHover
                            highlightOnHoverColor="var(--clr-gray-100)">
                            <Table.Thead>
                              <Table.Tr bg={"var(--clr-gray-100)"}>
                                <Table.Th fw={500}>{t("service")}</Table.Th>
                                <Table.Th fw={500}>{t("consumables")}</Table.Th>
                                <Table.Th fw={500}>{t("quantity")}</Table.Th>
                                <Table.Th fw={500} w={100} ta={"right"}>
                                  {t("action")}
                                </Table.Th>
                              </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                              {renderLoadingErrorData({
                                isLoading: isInjectionsLoading,
                                isError: isInjectionsError,
                                isSuccess: isInjectionsSuccess,
                                dataLength: injectionsData?.length ?? 0,
                                errorComponent: (
                                  <Table.Tr mt={"1rem"}>
                                    <Table.Td colSpan={5}>
                                      <Text
                                        fz={15}
                                        c="dimmed"
                                        lh={1.5}
                                        ta={"center"}>
                                        {t("encountered-error")}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ),
                                emptyDataComponent: (
                                  <Table.Tr mt={"1rem"}>
                                    <Table.Td colSpan={5}>
                                      <Text
                                        fz={15}
                                        c="dimmed"
                                        lh={1.5}
                                        ta={"center"}>
                                        {t("empty-data")}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ),
                                loadingComponent: (
                                  <Table.Tr mt={"1rem"}>
                                    <Table.Td colSpan={5}>
                                      <Text
                                        fz={15}
                                        c="dimmed"
                                        lh={1.5}
                                        ta={"center"}>
                                        {t("loading")}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ),
                                dataComponent: injectionsRows,
                              })}
                            </Table.Tbody>
                          </Table>
                        </>
                      ) : null}
                    </div>
                    </Tabs.Panel>
                  </Tabs>
                </>
              ) : null}

              {step === 6 ? (
                <div className="form-step">
                  <div className="form-field">
                    <TagsInput
                      color="accent"
                      label={t("diagnosis")}
                      placeholder={t("type-tags")}
                      styles={{
                        inputField: {
                          boxShadow: "none !important",
                        },
                      }}
                      value={diagnoses}
                      onChange={setDiagnoses}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="doctors-advice">
                      {t("clinical-recommendation")} <sup>*</sup>
                    </label>
                    <textarea
                      id="doctors-advice"
                      ref={recommendationFieldRef}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus={isRecommendationFieldFocused}
                      rows={5}
                      name="recommendation"
                      value={formData?.recommendation ?? ""}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                    />
                  </div>
                  <CreateAppointmentModal
                    prePatientId={patientId as string}
                    preCurrentTab="existing"
                    opened={createAppointmentModalOpened}
                    close={createAppointmentModalClose}
                  />

                  <Tabs
                    defaultValue="prescription"
                    mt="2rem"
                    styles={{
                      panel: {
                        marginTop: "2rem",
                      },
                    }}>
                    <Tabs.List>
                      <Tabs.Tab value="prescription">
                        {t("prescription")}
                      </Tabs.Tab>
                      <Tabs.Tab value="treatment">
                        {t("treatment-plan")}
                      </Tabs.Tab>
                      <Tabs.Tab value="follow-up">
                        {t("follow-up-appointment")}
                      </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="follow-up">
                      <Text fz={14} mt="2rem">
                        {t("click-to-create-follow-up-appointment")}
                      </Text>
                      <PermissionGate permission="appointments.create"><Button
                        variant="outline"
                        w="fit-content"
                        mt="1rem"
                        onClick={createAppointmentModalOpen}>
                        {t("create-appointment")}
                      </Button></PermissionGate>
                      <EditFollowUpAppointmentModal
                        opened={editFollowUpAppointmentModalOpened}
                        close={closeEditFollowUpAppointmentModal}
                        appointment={currentFollowUpAppointment}
                      />

                      <Table
                        mt={"2rem"}
                        withRowBorders={true}
                        withTableBorder
                        highlightOnHover
                        highlightOnHoverColor="var(--clr-gray-100)">
                        <Table.Thead>
                          <Table.Tr bg={"var(--clr-gray-100)"}>
                            <Table.Th fw={500}>
                              {t("appointment-date")}
                            </Table.Th>
                            <Table.Th fw={500}>{t("created-at")}</Table.Th>
                            <Table.Th fw={500}>{t("doctor")}</Table.Th>
                            <Table.Th fw={500}></Table.Th>
                          </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                          {renderLoadingErrorData({
                            isLoading: isFollowUpAppointmentsLoading,
                            isError: isFollowUpAppointmentsError,
                            isSuccess: isFollowUpAppointmentsSuccess,
                            dataLength:
                              followUpAppointmentsData?.pagination?.total ?? 0,
                            errorComponent: (
                              <Table.Tr mt={"1rem"}>
                                <Table.Td colSpan={4}>
                                  <Text
                                    fz={15}
                                    c="dimmed"
                                    lh={1.5}
                                    ta={"center"}>
                                    {t("encountered-error")}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ),
                            emptyDataComponent: (
                              <Table.Tr mt={"1rem"}>
                                <Table.Td colSpan={4}>
                                  <Text
                                    fz={15}
                                    c="dimmed"
                                    lh={1.5}
                                    ta={"center"}>
                                    {t("empty-data")}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ),
                            loadingComponent: (
                              <Table.Tr mt={"1rem"}>
                                <Table.Td colSpan={4}>
                                  <Text
                                    fz={15}
                                    c="dimmed"
                                    lh={1.5}
                                    ta={"center"}>
                                    {t("loading")}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ),
                            dataComponent: followUpAppointmentRows,
                          })}
                        </Table.Tbody>
                      </Table>
                    </Tabs.Panel>

                    <Tabs.Panel value="treatment">
                      <Text fz={14} mt="2rem">
                        {t("click-to-create-treatment-plan")}
                      </Text>

                      <PermissionGate permission="treatments.create"><Button
                        disabled={isTreatmentPlansLoading}
                        variant="outline"
                        leftSection={<IconPlus size={14} />}
                        w="fit-content"
                        mt={"1rem"}
                        onClick={openAddTreatmentPlanModal}>
                        {t("create-treatment-plan")}
                      </Button></PermissionGate>
                      <AddTreatmentPlanModal
                        opened={addTreatmentPlanModalOpened}
                        close={closeAddTreatmentPlanModal}
                        supportStaffData={supportStaffComboboxItems ?? []}
                      />

                      <Table
                        mt={"2rem"}
                        withRowBorders={true}
                        withTableBorder
                        highlightOnHover
                        highlightOnHoverColor="var(--clr-gray-100)">
                        <Table.Thead>
                          <Table.Tr bg={"var(--clr-gray-100)"}>
                            <Table.Th fw={500}>{t("treatment")}</Table.Th>
                            <Table.Th fw={500}>{t("quantity")}</Table.Th>
                            <Table.Th fw={500}>{t("support-staff")}</Table.Th>
                            <Table.Th fw={500}>{t("date")}</Table.Th>
                            <Table.Th fw={500} w={100} ta={"right"}>
                              {/* {t("action")} */}
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                          {renderLoadingErrorData({
                            isLoading: isTreatmentPlansLoading,
                            isError: isTreatmentPlansError,
                            isSuccess: isTreatmentPlansSuccess,
                            dataLength:
                              treatmentPlansData?.pagination.total ?? 0,
                            errorComponent: (
                              <Table.Tr mt={"1rem"}>
                                <Table.Td colSpan={5}>
                                  <Text
                                    fz={15}
                                    c="dimmed"
                                    lh={1.5}
                                    ta={"center"}>
                                    {t("encountered-error")}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ),
                            emptyDataComponent: (
                              <Table.Tr mt={"1rem"}>
                                <Table.Td colSpan={5}>
                                  <Text
                                    fz={15}
                                    c="dimmed"
                                    lh={1.5}
                                    ta={"center"}>
                                    {t("empty-data")}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ),
                            loadingComponent: (
                              <Table.Tr mt={"1rem"}>
                                <Table.Td colSpan={5}>
                                  <Text
                                    fz={15}
                                    c="dimmed"
                                    lh={1.5}
                                    ta={"center"}>
                                    {t("loading")}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ),
                            dataComponent: treatmentPlanRows,
                          })}
                        </Table.Tbody>
                      </Table>

                      <EditTreatmentPlanModal
                        opened={editTreatmentPlanModalOpened}
                        close={closeEditTreatmentPlanModal}
                        supportStaffData={supportStaffComboboxItems ?? []}
                        treatmentPlan={currentTreatmentPlan}
                      />
                    </Tabs.Panel>

                    <Tabs.Panel value="prescription">
                      <div className="form-step">
                        <AddMedicationModal
                          opened={addMedicationModalOpened}
                          close={addMedicationModalClose}
                          prescriptionId={prescriptionData?.id ?? ""}
                          cId={formData?.cId ?? ""}
                          shouldCreatePrescription={shouldCreatePrescription}
                          generalConsultationInfo={{
                            userId: userId ?? "",
                            patientId,
                            id: formData?.id,
                          }}
                        />

                        {isPrescriptionLoading ||
                        (prescriptionData && isMedicationsLoading) ? (
                          <Loader size={18} />
                        ) : null}

                        {!isMedicationsLoading &&
                        isMedicationsSuccess &&
                        medicationsData?.newData?.length ? (
                          <>
                            <Stack gap={"xs"}>
                              <Text fw={500}>{t("prescription")}</Text>
                              <Text fz={14} c="dimmed" lh={1.5}>
                                {t("prescription-add-meds")}
                              </Text>
                              <PermissionGate permission="medications.create"><Button
                                mt={"sm"}
                                variant="outline"
                                w="fit-content"
                                onClick={() => {
                                  setShouldCreatePrescription(false);
                                  addMedicationModalOpen();
                                }}
                                leftSection={<IconPlus size={18} />}>
                                {t("add-medication")}
                              </Button></PermissionGate>
                            </Stack>
                            <ul className="table">
                              <li className="table-header">
                                <p>{t("medication")}</p>
                                <p>{t("dosage")}</p>
                                <span></span>
                              </li>
                              <EditMedicationModal
                                opened={editMedicationModalOpened}
                                close={editMedicationModalClose}
                                medication={selectedMedication}
                                cId={formData?.cId ?? ""}
                              />

                              {medicationsData?.newData?.map(medication => (
                                <li className="table-row" key={medication.id}>
                                  <p>
                                    {/* {prescription.files?.length ? (
                            <GrAttachment size={14} color="var(--clr-accent)" />
                          ) : null} */}
                                    {truncateWithEllipsis(medication.name, 25)}
                                  </p>
                                  <p>
                                    {truncateWithEllipsis(
                                      medication.dosage,
                                      20
                                    )}
                                  </p>
                                  <span>
                                    <Group gap="0.25rem">
                                      <Tooltip
                                        label={t("edit-medication")}
                                        withArrow>
                                        <ActionIcon
                                          disabled={
                                            isDeleteMedicationLoading &&
                                            currentLabOrPrescId ===
                                              medication.id
                                          }
                                          variant="outline"
                                          onClick={() => {
                                            setSelectedMedication(medication);
                                            editMedicationModalOpen();
                                          }}>
                                          <IconPencil size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                      <Tooltip
                                        label={t("delete-medication")}
                                        withArrow>
                                        <PermissionGate permission="medications.delete"><ActionIcon
                                          loading={
                                            isDeleteMedicationLoading &&
                                            currentLabOrPrescId ===
                                              medication.id
                                          }
                                          color="red"
                                          variant="outline"
                                          onClick={() => {
                                            const medicationToDelete =
                                              medication.name;
                                            setCurrentLabOrPrescId(
                                              medication.id
                                            );
                                            openDeleteModal({
                                              textNode: (
                                                <Text fz={"sm"}>
                                                  <Trans
                                                    i18nKey={
                                                      "delete-medication-message"
                                                    }
                                                    values={{
                                                      medicationToDelete,
                                                    }}
                                                    components={{
                                                      bold: (
                                                        <Text
                                                          fz={"inherit"}
                                                          fw={500}
                                                          lh={1.5}
                                                          span
                                                        />
                                                      ),
                                                    }}
                                                  />
                                                </Text>
                                              ),
                                              onConfirm: () => {
                                                const id =
                                                  showLoadingNotification({});
                                                deleteMedication(
                                                  {
                                                    medicationId: medication.id,
                                                    prescriptionId:
                                                      medication.prescriptionId,
                                                    patientId:
                                                      patientId as string,
                                                  },
                                                  {
                                                    onSuccess: () => {
                                                      showSuccessNotification({
                                                        id,
                                                        title: "success",
                                                        message:
                                                          "delete-medication-success",
                                                      });
                                                    },
                                                    onError: (res: any) => {
                                                      handleErrors(
                                                        res,
                                                        "mutation",
                                                        id
                                                      );
                                                    },
                                                  }
                                                );
                                              },
                                            });
                                          }}>
                                          <IconTrash size={16} />
                                        </ActionIcon></PermissionGate>
                                      </Tooltip>
                                    </Group>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : null}

                        {!isPrescriptionLoading && isPrescriptionError ? (
                          <Stack gap={"xs"}>
                            <Text fw={500}>{t("prescription")}</Text>
                            <Text fz={14} c="dimmed" lh={1.5}>
                              {t("empty-consultation-prescription")}
                            </Text>
                            <Alert
                              mt={"md"}
                              w={"fit-content"}
                              variant="light"
                              color="blue"
                              icon={<IconInfoCircle />}>
                              <Text fz={14} lh={1.5} c="blue">
                                {t("auto-prescription-creation")}
                              </Text>
                            </Alert>
                            <PermissionGate permission="medications.create"><Button
                              variant="outline"
                              mt="sm"
                              w="fit-content"
                              onClick={() => {
                                setShouldCreatePrescription(true);
                                addMedicationModalOpen();
                              }}
                              leftSection={<IconPlus size={18} />}>
                              {t("add-medication")}
                            </Button></PermissionGate>
                          </Stack>
                        ) : null}

                        {isPrescriptionSuccess &&
                        isMedicationsSuccess &&
                        !medicationsData?.newData?.length ? (
                          <Stack gap={"xs"}>
                            <Text fw={500}>{t("prescription")}</Text>
                            <Text fz={14} c="dimmed" lh={1.5}>
                              {t("prescription-created-empty-meds")}
                            </Text>

                            <PermissionGate permission="medications.create"><Button
                              variant="outline"
                              mt="sm"
                              w="fit-content"
                              onClick={() => {
                                setShouldCreatePrescription(false);
                                addMedicationModalOpen();
                              }}
                              leftSection={<IconPlus size={18} />}>
                              {t("add-medication")}
                            </Button></PermissionGate>
                          </Stack>
                        ) : null}
                      </div>
                    </Tabs.Panel>
                  </Tabs>
                </div>
              ) : null}

              {step === 7 ? (
                <div className="form-step">
                  <Tabs defaultValue="files" w={"50%"}>
                    <Tabs.List>
                      <Tabs.Tab value="files">{t("files")}</Tabs.Tab>
                      <Tabs.Tab value="sick-notes">{t("sick-notes")}</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="files">
                      <Text fz={14} mt={"2rem"}>
                        {t("click-to-add-file")}
                      </Text>
                      <AddFileModal
                        opened={addFileModalOpened}
                        close={closeAddFileModal}
                      />

                      <PermissionGate permission={consultationId ? "consultations.edit" : "consultations.create"}><Button
                        mt={"1rem"}
                        onClick={openAddFileModal}
                        variant="outline">
                        {t("add-file")}
                      </Button></PermissionGate>

                      <div className="files-wrapper">
                        <div className="uploaded-files">
                          <h4>{t("previously-uploaded-files")}</h4>

                          {consultationData?.files?.length ? (
                            <ul>
                              {consultationData?.files
                                ?.reverse()
                                ?.map((file, index) => (
                                  <li key={index}>
                                    <div className="file-content">
                                      {(file as string).slice(-3) === "pdf" ? (
                                        <AiOutlineFilePdf />
                                      ) : (
                                        <AiOutlineFileImage />
                                      )}
                                      <a
                                        target="_blank"
                                        rel="noreferrer"
                                        href={
                                          "https://" + file + `?token=${token}`
                                        }>
                                        {truncateWithEllipsis(
                                          (file as string).split("/")[3],
                                          MAX_FILENAME_LENGTH_AFTER_UPLOAD
                                        )}
                                      </a>
                                    </div>

                                    <Tooltip disabled label="" withArrow>
                                      <ActionIcon
                                        color="red"
                                        disabled
                                        variant="outline">
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </li>
                                ))}
                            </ul>
                          ) : (
                            <Text c="dimmed" fz={14} mt={"md"}>
                              {t("empty-previously-uploaded-files")}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Tabs.Panel>

                    <Tabs.Panel value="sick-notes" className="sick-notes">
                      <Text mt="2rem" fz={14}>
                        Click on the button below to create a sick note.
                      </Text>

                      <PermissionGate permission="sick_notes.create"><Button
                        mt="1rem"
                        onClick={openAddSickNoteModal}
                        variant="outline">
                        {t("create-sick-note")}
                      </Button></PermissionGate>
                      <SickNoteModal
                        opened={addSickNoteModalOpened}
                        close={closeAddSickNoteModal}
                      />

                      <div className="files-wrapper">
                        <div className="uploaded-files">
                          <h4>Previous sick notes</h4>
                          <SickNoteModal
                            sickNote={currentSickNote}
                            opened={editSickNoteModalOpened}
                            close={closeEditSickNoteModal}
                          />

                          {sickNotesData?.length ? (
                            <ul>
                              {sickNotesData?.map(note => {
                                return note.files?.map((file, index) => {
                                  return (
                                    <li key={index}>
                                      <div className="file-content">
                                        {(file as string).slice(-3) ===
                                        "pdf" ? (
                                          <AiOutlineFilePdf />
                                        ) : (
                                          <AiOutlineFileImage />
                                        )}
                                        <a
                                          target="_blank"
                                          rel="noreferrer"
                                          href={
                                            "https://" +
                                            file +
                                            `?token=${token}`
                                          }>
                                          {truncateWithEllipsis(
                                            (file as string).split("/")[3],
                                            MAX_FILENAME_LENGTH_AFTER_UPLOAD - 5
                                          )}
                                        </a>
                                      </div>

                                      <Group gap={5}>
                                        <Tooltip
                                          label="Edit sick note"
                                          withArrow>
                                          <ActionIcon
                                            color="primary"
                                            variant="outline"
                                            onClick={() => {
                                              setCurrentSickNote(note);
                                            }}>
                                            <IconPencil
                                              size={16}
                                              stroke={1.5}
                                            />
                                          </ActionIcon>
                                        </Tooltip>
                                      </Group>
                                    </li>
                                  );
                                });
                              })}
                            </ul>
                          ) : (
                            <Text c="dimmed" fz={14} mt={"md"}>
                              {t("empty-previously-uploaded-files")}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Tabs.Panel>
                  </Tabs>
                </div>
              ) : null}

              <div className="footer">
                <Button
                  variant="transparent"
                  size="compact-sm"
                  disabled={
                    step === TOTAL_STEPS ||
                    (step === 1 &&
                      //. !state?.selectedConsultation &&
                      !isCreateConsultationSuccess) ||
                    !date ||
                    !formData?.consultationService?.id
                  }
                  onClick={e => {
                    e.preventDefault();
                    if (
                      step === TOTAL_STEPS ||
                      (step === 1 && !isCreateConsultationSuccess)
                    )
                      return;
                    setStep(TOTAL_STEPS);
                    setSessionStep(String(TOTAL_STEPS));
                  }}>
                  {t("skip-to-end")}
                </Button>
                <div className="wrapper">
                  {step > 1 ? (
                    <Button
                      variant="outline"
                      disabled={
                        isCreateConsultationLoading ||
                        isUpdateConsultationLoading
                      }
                      onClick={e => {
                        e.preventDefault();
                        if (step === 1) return;
                        setStep(step - 1);
                        setSessionStep(String(step - 1));
                      }}>
                      {t("previous")}
                    </Button>
                  ) : null}
                  <Button.Group>
                    <PermissionGate permission={consultationId ? "consultations.edit" : "consultations.create"}><Button
                      type="submit"
                      disabled={
                        (isConsultationLoading &&
                          //. !state?.selectedConsultation &&
                          !!consultationId) ||
                        (step === TOTAL_STEPS &&
                          formData?.status === "complete")
                      }
                      loading={
                        isCreateConsultationLoading ||
                        isUpdateConsultationLoading
                      }>
                      {getButtonText()}
                    </Button></PermissionGate>
                    <PermissionGate permission={consultationId ? "consultations.edit" : "consultations.create"}><Button
                      type="button"
                      px={"xs"}
                      style={{ borderLeft: "1px solid rgb(255,255,255,0.5)" }}
                      onClick={saveForm}
                      loading={
                        isUpdateConsultationLoading ||
                        isCreateConsultationLoading
                      }>
                      <IconDeviceFloppy size={22} stroke={1.5} />
                    </Button></PermissionGate>
                  </Button.Group>
                </div>
              </div>
            </form>
          </div>
        </StyledGeneralConsultation>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
